import React, { useState } from 'react';
import { usePersonas } from '../../hooks/usePersonas';
import { useRunSimulation } from '../../hooks/useSimulations';
import { FileUpload } from '../common/FileUpload';
import type { SimulationFormat } from '../../types';

interface SimulationRunnerProps {
  projectId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export const SimulationRunner: React.FC<SimulationRunnerProps> = ({
  projectId,
  onComplete,
  onCancel,
}) => {
  const { personas } = usePersonas(projectId);
  const { runSimulation, loading, error } = useRunSimulation();

  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [format, setFormat] = useState<SimulationFormat>('focus_group');
  const [assetUrl, setAssetUrl] = useState('');
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [assetSource, setAssetSource] = useState<'url' | 'upload'>('url');
  const [showAssetSection, setShowAssetSection] = useState(false);
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !topic.trim() || selectedPersonas.length === 0) {
      return;
    }

    // Use uploaded file URL if available, otherwise use the URL field
    const finalAssetUrl = uploadedFileUrl || assetUrl.trim() || undefined;

    try {
      await runSimulation({
        projectId,
        title: title.trim(),
        topic: topic.trim(),
        format,
        assetUrl: finalAssetUrl,
        personaIds: selectedPersonas,
      });
      onComplete();
    } catch (err) {
      console.error('Failed to run simulation:', err);
    }
  };

  const togglePersona = (personaId: string) => {
    setSelectedPersonas((prev) =>
      prev.includes(personaId)
        ? prev.filter((id) => id !== personaId)
        : [...prev, personaId]
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">New Simulation</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Product Feedback Discussion"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {/* Topic */}
          <div className="mb-4">
            <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-2">
              Topic / Question
            </label>
            <textarea
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., What would make you try a new product in this category?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {/* Format */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="focus_group"
                  checked={format === 'focus_group'}
                  onChange={(e) => setFormat(e.target.value as SimulationFormat)}
                  className="mr-2"
                />
                <span className="text-sm">Focus Group Discussion</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="survey"
                  checked={format === 'survey'}
                  onChange={(e) => setFormat(e.target.value as SimulationFormat)}
                  className="mr-2"
                />
                <span className="text-sm">Individual Survey</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {format === 'focus_group'
                ? 'Personas discuss together with natural dialogue'
                : 'Each persona responds independently'}
            </p>
          </div>

          {/* Optional Asset Section */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowAssetSection(!showAssetSection)}
              className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <svg
                className={`mr-2 h-5 w-5 transform transition-transform ${showAssetSection ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Add Asset to Discuss (Optional)
            </button>
            <p className="ml-7 text-xs text-gray-500 mt-1">
              Include an image, video, or document for personas to react to
            </p>

            {showAssetSection && (
              <div className="ml-7 mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                {/* Tab buttons */}
                <div className="flex border-b border-gray-200 mb-4">
                  <button
                    type="button"
                    onClick={() => setAssetSource('url')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      assetSource === 'url'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Link URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssetSource('upload')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      assetSource === 'upload'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Upload File
                  </button>
                </div>

                {/* URL input */}
                {assetSource === 'url' && (
                  <div>
                    <input
                      type="url"
                      value={assetUrl}
                      onChange={(e) => setAssetUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Link to image, video, or document
                    </p>
                  </div>
                )}

                {/* File upload */}
                {assetSource === 'upload' && (
                  <FileUpload
                    projectId={projectId}
                    onUploadComplete={(url, fileName, fileType) => {
                      setUploadedFileUrl(url);
                      setUploadedFileName(fileName);
                    }}
                    onUploadError={(error) => {
                      console.error('File upload error:', error);
                    }}
                    acceptedTypes="image/*,video/*,.pdf,.doc,.docx"
                    maxSizeMB={10}
                    label=""
                    helperText="Upload asset for personas to discuss"
                  />
                )}
              </div>
            )}
          </div>

          {/* Persona Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Personas ({selectedPersonas.length} selected)
            </label>
            <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              {personas.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No personas available. Generate personas first.
                </p>
              ) : (
                personas.map((persona) => (
                  <label
                    key={persona.personaId}
                    className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPersonas.includes(persona.personaId)}
                      onChange={() => togglePersona(persona.personaId)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{persona.name}</div>
                      <div className="text-xs text-gray-500">
                        {persona.demographics.age} • {persona.demographics.occupation}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !topic.trim() || selectedPersonas.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Running Simulation...' : 'Run Simulation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
